import { ReachAPI } from '../ReachApi';
import { labOptions } from '../Signage';
import { Log } from '../AuxillaryService/Log';
import { $ } from '../../vendor/jquery';
import asmCrypto from 'asmcrypto-lite';
import { xhrGetResponseHeader } from '../Utility';

/**
 * Successful Response
 *
 * Returns 3 values:
 *
 * 1. {Blob} blob
 * 2. {string} textStatus
 * 3. {XMLHttpRequest} xhr
 * @typedef {jQuery.Promise} jqpSuccessAsset
 */

/**
 * Failure Response
 *
 * Returns 3 values:
 *
 * 1. {XMLHttpRequest} xhr
 * 2. {string} textStatus
 * 3. {Event} event
 * @typedef {jQuery.Promise} jqpFailureAsset
 */

/**
 * @class
 */
var Asset = (function () {
    var self = {};

    self.ERROR_INVALID_HASH = 'Invalid Hash';
    self.ERROR_HASHING_FAILED = 'Hashing Failed';
    self.ERROR_CONTENT_LENGTH = 'Incorrect Content Length';

    /**
     * List of Reach host names
     * @type {Array.<string|RegExp>}
     * @private
     * @memberof Asset
     */
    self._reachHosts = [
        'signs.reachcm.com',
        'r.reachcm.com',
        'api.reachcm.com',
        'papi.reachcm.com',
        /\.reachcm\.com$/,
    ];

    /**
     * List of Reach URLs that should be considered external links
     * @type {Array.<string|RegExp>}
     * @private
     * @memberof Asset
     */
    self._reachHostBlacklist = [/^(https?:)?\/\/api\.reachcm\.com\/socialmedia\//i];

    /**
     * List of host names to run through the proxy
     * @type {Array.<string|RegExp>}
     * @private
     * @memberof Asset
     */
    self._proxyHostWhitelist = [/\.screenfeed\.com$/];

    /**
     * Convert Blob object to a Data URL
     * @param {Blob} blob
     * @throws {jQuery.Promise<DOMError, Event>}
     * @returns {jQuery.Promise<string, Event>}
     *      Success `jQuery.Promise[{string} url, {Event} event]`
     * @memberof Asset
     */
    self.blobToDataURL = function (blob) {
        var jqDeferred = $.Deferred();
        var file = new FileReader();

        file.onload = function (event) {
            jqDeferred.resolve(event.target.result, event);
        };
        file.onerror = function (event) {
            jqDeferred.reject(event.target.error, event);
        };
        file.readAsDataURL(blob);

        return jqDeferred.promise();
    };

    /**
     * AJAX GET call to retrieve Blob
     * @param {string} url
     * @throws {jqpFailureAsset}
     * @returns {jqpSuccessAsset}
     * @memberof Asset
     */
    self.getBlob = function (url) {
        var jqDeferred = $.Deferred();
        var xhr = new XMLHttpRequest();

        // define request
        xhr.open('GET', url, true);
        xhr.responseType = 'blob';

        //NOTE: onload fires when HTTP req/resp is successful regardless of HTTP
        //      status code. CORS violations are an exception which are handled
        //      by onerror even though they are protocol-level errors, not
        //      network level errors.
        xhr.onload = function (event) {
            if (xhr.status === 200) {
                // Blob as response
                var blob = xhr.response;

                jqDeferred.resolve(blob, xhr.statusText, xhr);
            } else {
                // everything fails
                jqDeferred.reject(xhr, xhr.statusText, event);
            }
        };

        //NOTE: onerror is called when a network-level error occurs. This
        //      includes CORS failures (which are not technically "network
        //      level")
        xhr.onerror = function (event) {
            jqDeferred.reject(xhr, 'ERROR', event);
        };

        xhr.onabort = function (event) {
            jqDeferred.reject(xhr, 'CANCELED', event);
        };

        // Send XHR
        xhr.send();

        return jqDeferred.promise();
    };

    /**
     * Determine if a link is to an external site (ie. not a REACH site)
     * @param {string} url
     * @return {boolean}
     * @memberof Asset
     */
    self.isExternal = function (url) {
        var isInternalHost = self._findHostName(url, self._reachHosts);
        var isBlacklisted = self._isUrlInList(url, self._reachHostBlacklist);

        return !(isInternalHost && !isBlacklisted);
    };

    /**
     * Determine if a link's host is in the proxy whitelist
     * @param {string} url
     * @return {boolean}
     * @memberof Asset
     */
    self.requiresProxy = function (url) {
        return self._findHostName(url, self._proxyHostWhitelist);
    };

    /**
     * AJAX GET call to retrieve Blob (with code to resolve cross-domain and
     * mixed-content errors)
     * @param {string} url
     * @throws {jqpFailureAsset}
     * @returns {jqpSuccessAsset}
     * @memberof Asset
     */
    self.retrieve = function (url) {
        var jqDeferred = $.Deferred();
        var originalUrl = url;
        var isExternalAsset = self.isExternal(url);
        var isInternalAsset = !isExternalAsset;
        var assetRequiresProxy = self.requiresProxy(url);

        // prevent caching only on internal assets (some external sites like
        // Twitter have problems with Cache-Busting)
        if (isInternalAsset) {
            url = ReachAPI.urlWithParams(url, { _t: Date.now() });
        }

        // XMLHttpRequest fails on 'Mixed Content' errors, make sure we are
        // pulling from HTTPS
        //
        // TODO: this is questionable because there is no guarantee that the
        //       target server supports HTTPS or that the HTTPS version of the
        //       site is the same as the HTTP version of the site. Additionally,
        //       we only get Mixed Content errors if we are running in a regular
        //       web browser, not on BrightSign or REACH Signage Player. Perhaps
        //       do this logic if we're not running on BrightSign or REACH
        //       Player?
        if (location.protocol === 'https:') {
            url = url.replace(/^http:/, 'https:');
        }

        // override url with proxy (BrightSign has problems with with some
        // external sites)
        if (
            assetRequiresProxy ||
            (isExternalAsset && labOptions.instance.get('proxyExternalAssets'))
        ) {
            url =
                process.env.BASE_URL_API + '/signage/proxy?url=' + encodeURIComponent(originalUrl);
        }

        self.getBlob(url)
            .done(function (blob, statusText, xhr) {
                var contentLength;
                var contentSHA256Hash;

                // Get `X-Original-Content-Length` and `Content-SHA256-Hash` from
                // internal assets
                if (isInternalAsset) {
                    contentLength = xhrGetResponseHeader(xhr, 'X-Original-Content-Length');
                    contentSHA256Hash = xhrGetResponseHeader(xhr, 'Content-SHA256-Hash');

                    if (contentLength === null && contentSHA256Hash === null) {
                        Log.Debug(
                            `No data available to validate that the asset (${url}) downloaded correctly.`,
                            null,
                            __fileline,
                            url
                        );
                    }
                }

                if (isInternalAsset && contentLength && contentLength != blob.size) {
                    // failed to download image correctly
                    jqDeferred.reject(xhr, self.ERROR_CONTENT_LENGTH, null);
                } else if (contentSHA256Hash) {
                    // check hashes
                    self.sha256hash(blob)
                        .done(function (blobHash, event) {
                            // hashed blob
                            if (contentSHA256Hash != blobHash) {
                                // hashes do not match
                                jqDeferred.reject(xhr, self.ERROR_INVALID_HASH, event);
                            } else {
                                // success
                                jqDeferred.resolve(blob, statusText, xhr);
                            }
                        })
                        .fail(function (error, event) {
                            // failed to hash blob
                            jqDeferred.reject(xhr, self.ERROR_HASHING_FAILED, event);
                        });
                } else {
                    // success
                    jqDeferred.resolve(blob, statusText, xhr);
                }
            })
            .fail(function (xhr, statusText, event) {
                if ((statusText === 'ERROR' || statusText === 'CANCELED') && isExternalAsset) {
                    // maybe cross domain error, try again with proxy
                    Log.Warning(
                        "Failed to download '" +
                            url +
                            "'. Possible cross-domain or mixed-content error, trying again via proxy."
                    );

                    var proxyURL =
                        process.env.BASE_URL_API +
                        '/signage/proxy?url=' +
                        encodeURIComponent(originalUrl); // use proxy to bypass cross-domain errors

                    self.getBlob(proxyURL)
                        .done(function (blob, statusText, xhr) {
                            // success
                            jqDeferred.resolve(blob, statusText, xhr);
                        })
                        .fail(function (xhr, statusText, event) {
                            // everything fails
                            jqDeferred.reject(xhr, statusText, event);
                        });
                } else {
                    // fails
                    //NOTE: this case occurs if server returns something besides 200
                    Log.Warning(
                        'HTTP request failed for asset',
                        'SIGNAGE_ASSET_CACHE_HTTP_ERROR',
                        __fileline,
                        {
                            url: url,
                            status: xhr.status || null,
                            statusText: statusText,
                        }
                    );
                    jqDeferred.reject(xhr, statusText, event);
                }
            });

        return jqDeferred.promise();
    };

    /**
     * Hash Blob via SHA256
     * @param {Blob} blob
     * @throws {jQuery.Promise<DOMError, Event>}
     * @returns {jQuery.Promise<string, Event>}
     *      Success `jQuery.Promise[{string} hash, {Event} event]`
     * @memberof Asset
     */
    self.sha256hash = function (blob) {
        var jqDeferred = $.Deferred();
        var file = new FileReader();

        file.onload = function (event) {
            var arrayBuffer = event.target.result;
            var hash = asmCrypto.SHA256.hex(arrayBuffer);

            jqDeferred.resolve(hash, event);
        };

        file.onerror = function (event) {
            jqDeferred.reject(event.target.error, event);
        };

        file.readAsArrayBuffer(blob);

        return jqDeferred.promise();
    };

    /**
     * Determine if a link's host is in the given list of hosts
     * @param {string} url
     * @param {Array.<string|RegExp>} hostList
     * @return {boolean}
     * @private
     * @memberof Asset
     */
    self._findHostName = function (url, hostList) {
        var link = document.createElement('a');
        var givenHost;

        link.href = url;
        givenHost = link.hostname.toLowerCase();

        return self._isUrlInList(givenHost, hostList);
    };

    /**
     * Determine if a URL is in the given list of URLs
     * @param {string} url
     * @param {Array.<string|RegExp>} urlList
     * @return {boolean}
     * @private
     * @memberof Asset
     */
    self._isUrlInList = function (url, urlList) {
        var idx;
        var urlItem;

        for (idx = 0; idx < urlList.length; idx++) {
            urlItem = urlList[idx];

            if (urlItem instanceof RegExp) {
                if (urlItem.test(url)) {
                    return true;
                }
            } else if (typeof urlItem === 'string') {
                if (urlItem === url) {
                    return true;
                }
            }
        }

        return false;
    };

    return self;
})();

export { Asset };
